import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReportDraft, ReportDraftSection } from '@officehub/shared';
import type { ReportWizardApi } from './types';

const AUTOSAVE_MS = 1500;
/** survey_updateReportDraft refuses more sections than this. */
export const MAX_SECTIONS = 50;
const RETRY_BASE_MS = 3000;
const MAX_AUTOSAVE_RETRIES = 3;

/**
 * The draft being edited, and its autosave. Every edit is saved 1.5 s after the
 * last change, a pending save is flushed before the PDF is generated, and a
 * pending save fires when the wizard closes. (The Sales wizard's fix for audit
 * finding D3: edits used to live only in memory, and back/phone-lock lost them.)
 * The payload is always the full sections and photo selections, as before.
 */
export function useReportDraftState(initial: ReportDraft | null, saveDraft: ReportWizardApi['saveDraft']) {
  const [draft, setDraft] = useState<ReportDraft | null>(initial);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveRef = useRef(saveDraft);
  saveRef.current = saveDraft;

  const persist = useCallback(async () => {
    const d = draftRef.current;
    if (!d) return;
    await saveRef.current({ sections: d.sections, photoSelections: d.photoSelections });
  }, []);

  // An edit is "unsaved" from the moment it is made until a save succeeds.
  const unsavedRef = useRef(false);
  const retriesRef = useRef(0);

  /**
   * Background autosave. A failure retries after 3 s, 6 s, then 12 s, and then
   * stops (logged); the edit stays unsaved, so closing the wizard or generating the
   * PDF still tries once more.
   */
  const persistQuietly = useCallback(async (): Promise<void> => {
    try {
      await persist();
      unsavedRef.current = false;
      retriesRef.current = 0;
    } catch (err) {
      console.warn('ReportWizard: draft autosave failed', err);
      if (retriesRef.current < MAX_AUTOSAVE_RETRIES) {
        const delay = RETRY_BASE_MS * 2 ** retriesRef.current;
        retriesRef.current += 1;
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          void persistQuietly();
        }, delay);
      }
    }
  }, [persist]);

  const schedule = useCallback(() => {
    unsavedRef.current = true;
    retriesRef.current = 0;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      void persistQuietly();
    }, AUTOSAVE_MS);
  }, [persistQuietly]);

  /**
   * Save now, and THROW if the save fails. Generate PDF relies on this: rendering a
   * PDF after a failed save would send the customer a report missing the latest edits.
   */
  const flush = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    await persist();
    unsavedRef.current = false;
    retriesRef.current = 0;
  }, [persist]);

  // Closing the wizard sends any edit not yet saved, including one whose autosave failed.
  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    if (unsavedRef.current) {
      retriesRef.current = MAX_AUTOSAVE_RETRIES; // one last try, no timers after unmount
      void persistQuietly();
    }
  }, [persistQuietly]);

  /** Apply an edit to the draft and schedule its save. */
  const edit = useCallback((change: (d: ReportDraft) => ReportDraft) => {
    // Update the ref synchronously too, so a save that fires before React
    // re-renders (e.g. on unmount) still sends this edit.
    const next = draftRef.current ? change(draftRef.current) : null;
    if (!next) return;
    draftRef.current = next;
    setDraft(next);
    schedule();
  }, [schedule]);

  const updateSection = useCallback((id: string, updates: Partial<ReportDraftSection>) =>
    edit((d) => ({ ...d, sections: d.sections.map((s) => (s.id === id ? { ...s, ...updates } : s)), updatedAt: new Date().toISOString() })), [edit]);

  const deleteSection = useCallback((id: string) =>
    edit((d) => ({ ...d, sections: d.sections.filter((s) => s.id !== id) })), [edit]);

  const addSection = useCallback(() =>
    edit((d) => (d.sections.length >= MAX_SECTIONS ? d : {
      ...d,
      sections: [...d.sections, {
        id: `custom_${Date.now()}`,
        title: 'New Section',
        content: '<p></p>',
        order: Math.max(0, ...d.sections.map((s) => s.order)) + 1,
        source: 'manual' as const,
      }],
      updatedAt: new Date().toISOString(),
    })), [edit]);

  /** Swap a section with its neighbour in display order. */
  const moveSection = useCallback((id: string, direction: -1 | 1) =>
    edit((d) => {
      const ordered = [...d.sections].sort((a, b) => a.order - b.order);
      const i = ordered.findIndex((s) => s.id === id);
      const j = i + direction;
      if (i < 0 || j < 0 || j >= ordered.length) return d;
      const a = ordered[i];
      const b = ordered[j];
      return {
        ...d,
        sections: d.sections.map((s) => (s.id === a.id ? { ...s, order: b.order } : s.id === b.id ? { ...s, order: a.order } : s)),
        updatedAt: new Date().toISOString(),
      };
    }), [edit]);

  const togglePhoto = useCallback((photoId: string) =>
    edit((d) => ({ ...d, photoSelections: d.photoSelections.map((p) => (p.photoId === photoId ? { ...p, included: !p.included } : p)) })), [edit]);

  const assignPhoto = useCallback((photoId: string, sectionId: string | undefined) =>
    edit((d) => ({
      ...d,
      photoSelections: d.photoSelections.map((p) => {
        if (p.photoId !== photoId) return p;
        const { sectionId: _drop, ...rest } = p;
        return sectionId ? { ...rest, sectionId } : rest;
      }),
    })), [edit]);

  /** Replace the draft from a server result (draft, rewrite, revise): no autosave. */
  const replace = useCallback((next: ReportDraft | ((d: ReportDraft) => ReportDraft)) => {
    const value = typeof next === 'function' ? (draftRef.current ? next(draftRef.current) : null) : next;
    draftRef.current = value;
    setDraft(value);
  }, []);

  return { draft, flush, replace, updateSection, deleteSection, addSection, moveSection, togglePhoto, assignPhoto };
}
